import {AutoComplete, Button, Checkbox, Drawer, Flex, Input, Modal, Select, Table, TableColumnsType, Tag, Tooltip} from 'antd'
import {SettingOutlined} from '@ant-design/icons'
import {casinoKeyService, casinosConfig} from "../components/api";
import ResultModal from "../components/result-modal";
import {useEffect, useState} from "react";
import {ErrorView} from "../components/show-error";
import StringSelector from "../components/string-selector";
import {useSearchParams} from "react-router";
import {
  Action,
  MergeSettingsResponse_Delta,
  Result,
  SettingType,
  SettingTypeSchema
} from '../api/ga/config_manager/casinosconfig/casinos_config_pb'
import {NewCasinoKeyResponse} from '../api/ga/config_manager/casinokey/casinokey_pb'
import {DataEntryParent} from "../components/data-entry-parent";
import {AVAILABLE_TAGS} from "../components/tags";
import BranchTagsWidget from "../components/branch-tags-widget";
import {useHasPermission} from "../components/state";
import {Permission} from "../components/permissions";

export default function CasinoSettingsView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const canMerge = useHasPermission(Permission.DevToolsCasinoConfigMerge)

  const [modal, contextHolder] = Modal.useModal()

  const [rows, setRows] = useState([] as DataType[])
  const [envs, setEnvs] = useState([] as string[])
  const [branches, setBranches] = useState([] as string[])
  const [branchTags, setBranchTags] = useState({} as Record<string, string[]>)
  const [env, setEnv] = useState('')
  const [branch, setBranch] = useState('')
  const [newBranchOpen, setNewBranchOpen] = useState(false)
  const [newBranchName, setNewBranchName] = useState('')

  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false)
  const [branchTagsOpen, setBranchTagsOpen] = useState(true)

  const [tagsOpen, setTagsOpen] = useState(false)
  const [selectedTags, setSelectedTags] = useState([] as string[])

  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeDeltas, setMergeDeltas] = useState([] as MergeSettingsResponse_Delta[])
  const [mergeParams, setMergeParams] = useState({ env: '', fromBranch: '', toBranch: '' })

  const [validationOpen, setValidationOpen] = useState(false)
  const [validationRes, setValidationRes] = useState<Result | undefined>(undefined)

  const [exportOpen, setExportOpen] = useState(false)
  const [exportRes, setExportRes] = useState<Result | undefined>(undefined)
  const [exportLoading, setExportLoading] = useState(false)
  const [mergeLoading, setMergeLoading] = useState(false)

  const [importOpen, setImportOpen] = useState(false)
  const [importRes, setImportRes] = useState<Result | undefined>(undefined)
  const [importAskOpen, setImportAskOpen] = useState(false)
  const [importFromGitBranch, setImportFromGitBranch] = useState('master')
  const [importLoading, setImportLoading] = useState(false)

  const [tableSearch, setTableSearch] = useState('')

  const token_main_name = 'auth_token'
  const token_secondary_name = 'auth_token_secondary'
  const token_settings_main_name = 'settings.'+token_main_name
  const token_settings_secondary_name = 'settings.'+token_secondary_name
  const [secondaryTokensOpen, setSecondaryTokensOpen] = useState(false)
  const [createTokensLoadingKey, setCreateTokensLoadingKey] = useState<string | null>(null)
  const [addCasinoOpen, setAddCasinoOpen] = useState(false)
  const [newCasinoPath, setNewCasinoPath] = useState('')
  const [newCasinoId, setNewCasinoId] = useState('')
  const [newCasinoCheckResult, setNewCasinoCheckResult] = useState<NewCasinoKeyResponse | null>(null)

  const [masterRows, setMasterRows] = useState({} as Record<string, DataType>)
  const [modelMap, setModelMap] = useState(new Map<string, ModelInfo>())

  const [editOpen, setEditOpen] = useState(false)
  const [editRow, setEditRow] = useState<DataType | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editVaultPath, setEditVaultPath] = useState('')
  const [editVaultKey, setEditVaultKey] = useState('')

  interface ModelInfo {
    settingType: SettingType
    comment: string
    required: boolean
  }

  interface DataType {
    key: string
    branch: string // key
    env: string // key
    setting: string // key
    casino: string // key
    settingType: SettingType
    value: string
    vaultPath: string
    vaultKey: string
    action: Action
    virtual: boolean
    is_env_layer: boolean
    is_casino_layer: boolean
    children?: DataType[]
  }

  const loadSettings = () => {
    if (!branch) return
    casinosConfig.settings({ env, branch })
      .then((resp) => {
        const newModelMap = new Map<string, ModelInfo>()
        for (const m of resp.models ?? []) {
          newModelMap.set(m.setting, {
            settingType: m.settingType,
            comment: m.comment,
            required: m.required,
          })
        }
        setModelMap(newModelMap)

        type Row = {
          branch: string
          env: string
          casino: string
          setting: string
          settingType: SettingType
          value: string
          vaultPath: string
          vaultKey: string
          action: Action
          virtual: boolean
        }
        const flat: Row[] = []
        for (const s of resp.settings ?? []) {
          flat.push({
            branch: s.branch, env: s.env, casino: s.casino,
            setting: s.setting,
            settingType: newModelMap.get(s.setting)?.settingType ?? s.settingType,
            value: s.value, vaultPath: s.vaultPath, vaultKey: s.vaultKey,
            action: s.action,
            virtual: false,
          })
        }

        // Inject virtual rows from model schema for every (env, casino) tuple.
        {
          const realKeys = new Set<string>()
          const casinosByEnv = new Map<string, Set<string>>()
          for (const r of flat) {
            realKeys.add(`${r.env}:${r.casino}:${r.setting}`)
            if (!casinosByEnv.has(r.env)) casinosByEnv.set(r.env, new Set())
            casinosByEnv.get(r.env)!.add(r.casino)
          }
          casinosByEnv.forEach((cs, e) => {
            cs.forEach((c) => {
              for (const m of resp.models ?? []) {
                if (realKeys.has(`${e}:${c}:${m.setting}`)) continue
                flat.push({
                  branch: 'master', env: e, casino: c,
                  setting: m.setting, settingType: m.settingType,
                  value: '', vaultPath: '', vaultKey: '',
                  action: Action.NO_OR_UPSET,
                  virtual: true,
                })
              }
            })
          })
        }

        const treeMap = new Map<string, Map<string, Map<string, DataType>>>()
        const masterRowsMap = new Map<string, DataType>()

        const ensure = (r: Row, sett: string): DataType => {
          let casinoMap = treeMap.get(r.env)
          if (!casinoMap) {
            casinoMap = new Map()
            treeMap.set(r.env, casinoMap)
          }
          let settMap = casinoMap.get(r.casino)
          if (!settMap) {
            settMap = new Map()
            casinoMap.set(r.casino, settMap)
          }
          let node = settMap.get(sett)
          if (!node) {
            node = {
              key: `${r.env}:${r.casino}:${sett}`,
              branch: r.branch, env: r.env, casino: r.casino,
              setting: sett,
              settingType: SettingType.UNKNOWN,
              value: '', vaultPath: '', vaultKey: '',
              action: Action.NO_OR_UPSET,
              virtual: true,
              is_env_layer: false, is_casino_layer: false,
            }
            settMap.set(sett, node)
            const lastDot = sett.lastIndexOf('.')
            if (lastDot !== -1) {
              const parent = ensure(r, sett.substring(0, lastDot))
              parent.children = [...(parent.children ?? []), node]
            }
          }
          return node
        }

        for (const r of flat) {
          const node = ensure(r, r.setting)
          if (r.virtual) {
            if (node.settingType === SettingType.UNKNOWN) {
              node.settingType = r.settingType
            }
            continue
          }
          if (r.branch !== 'master' && !node.virtual && node.branch === 'master') {
            masterRowsMap.set(node.key, { ...node })
          }
          node.branch = r.branch
          node.settingType = r.settingType
          node.value = r.value
          node.vaultPath = r.vaultPath
          node.vaultKey = r.vaultKey
          node.action = r.action
          node.virtual = false
        }

        const sortBySetting = (a: DataType, b: DataType) => a.setting.localeCompare(b.setting)

        const roots: DataType[] = []
        treeMap.forEach((casinoMap, envKey) => {
          const casinoLayers: DataType[] = []
          let envBranch = 'master'
          casinoMap.forEach((settMap, casinoKey) => {
            const settingRoots: DataType[] = []
            let casinoBranch = 'master'
            settMap.forEach((node) => {
              if (node.children) node.children.sort(sortBySetting)
              if (node.setting.lastIndexOf('.') === -1) settingRoots.push(node)
              if (node.branch !== 'master') casinoBranch = node.branch
            })
            settingRoots.sort(sortBySetting)
            if (casinoBranch !== 'master') envBranch = casinoBranch
            casinoLayers.push({
              key: `${envKey}:${casinoKey}`,
              branch: casinoBranch, env: envKey, casino: casinoKey,
              setting: '',
              settingType: SettingType.UNKNOWN,
              value: '', vaultPath: '', vaultKey: '',
              action: Action.NO_OR_UPSET,
              virtual: false,
              is_env_layer: false, is_casino_layer: true,
              children: settingRoots,
            })
          })
          casinoLayers.sort((a, b) => a.casino.localeCompare(b.casino))
          roots.push({
            key: envKey,
            branch: envBranch, env: envKey, casino: '',
            setting: '',
            settingType: SettingType.UNKNOWN,
            value: '', vaultPath: '', vaultKey: '',
            action: Action.NO_OR_UPSET,
            virtual: false,
            is_env_layer: true, is_casino_layer: false,
            children: casinoLayers,
          })
        })
        roots.sort((a, b) => a.env.localeCompare(b.env))

        while (roots.length === 1 && roots[0].children?.length) {
          const children = roots[0].children
          roots.length = 0
          roots.push(...children)
        }

        setRows(roots)
        setMasterRows(Object.fromEntries(masterRowsMap))
      })
      .catch((e) => {
        ErrorView(modal, 'settings error', e)
      })
  }

  const loadBranches = (e?: string, b?: string) => {
    casinosConfig.settingsBranches({})
      .then((resp) => {
        const branchesMap = resp.branches ?? {}
        setEnvs(['', ...(resp.envs ?? [])])
        setBranches(Object.keys(branchesMap))
        setBranchTags(Object.fromEntries(
          Object.entries(branchesMap).map(([name, info]) => [name, info.tags ?? []])
        ))

        if (!branch && !b) {
          setBranch('master')
        }
      })
      .catch((e) => {
        ErrorView(modal, 'settingsBranches error', e)
      })
  }

  const casinoVaultPathOptions = (r: DataType): {value: string}[] => {
    if (r.env === 'dev' || r.env === 'demo') return [{value: `dev-shared/casinos/${r.casino}`}]
    return [{value: `${r.env}/casinos/${r.casino}`}]
  }

  const isCasinoVaultPathValid = (r: DataType, path: string): boolean => {
    if (r.env === 'dev' || r.env === 'demo') {
      return ['dev-shared', 'int'].some(env => {
        const prefix = `${env}/casinos/`
        const suffix = `/${r.casino}`
        if (!path.startsWith(prefix) || !path.endsWith(suffix)) return false
        const middle = path.slice(prefix.length, path.length - suffix.length)
        return middle.length > 0 && !/\s/.test(middle)
      })
    }
    const expected = `${r.env}/casinos/${r.casino}/`
    return path === expected || path + '/' === expected
  }

  const envOptions = Array.from(new Set(envs)).sort()

  useEffect(() => {
    const e = searchParams.get('env') ?? ''
    setEnv(e)
    const b = searchParams.get('branch') ?? ''
    setBranch(b)
    setTableSearch(searchParams.get('search') ?? '')
    loadBranches(e, b)
  }, [])

  // Sync branch from the URL so external navigation (e.g. the branch tags widget) updates the page.
  useEffect(() => {
    const b = searchParams.get('branch') ?? ''
    if (b && b !== branch) setBranch(b)
  }, [searchParams])

  useEffect(() => {
    setSearchParams((par) => {
      par.set('env', env)
      return par
    })
  }, [env])

  useEffect(() => {
    if (!branch) return

    setSearchParams((par) => {
      par.set('branch', branch)
      return par
    })
  }, [branch])

  useEffect(() => {
    setRows([])
    loadSettings()
  }, [env, branch])

  useEffect(() => {
    setSearchParams((par) => {
      if (tableSearch) par.set('search', tableSearch)
      else par.delete('search')
      return par
    })
  }, [tableSearch])

  const selectOptionsSettingTypeSchema: any[] | undefined = []
  SettingTypeSchema.values.forEach((val)=> {
    if (val.number == 0) return

    selectOptionsSettingTypeSchema.push({
      value: val.number,
      label: val.name.toLowerCase(),
    })
  })

  const columns: TableColumnsType<DataType> = [
    {
      title: 'env / casino / setting',
      dataIndex: 'setting',
      key: 'setting',
      width: '40vw',
      render: (_, row) => {
        if (row.is_env_layer) return row.env
        if (row.is_casino_layer) return row.casino
        const lastDot = row.setting.lastIndexOf('.')
        const label = lastDot !== -1 ? row.setting.substring(lastDot + 1) : row.setting
        const model = modelMap.get(row.setting)
        const text = model?.required ? <span style={{fontWeight: 'bold'}}>{label}</span> : label
        return model?.comment ? <Tooltip title={model.comment}>{text}</Tooltip> : text
      },
    },
    {
      title: 'value',
      dataIndex: 'value',
      key: 'value',
      width: '40vw',
      render: (_, row) => {
        const val = () => {
          if (row.virtual) {
            if (row.children) return ''
            return '<default>'
          }
          if (row.vaultPath || row.vaultKey) return `${row.vaultPath} . ${row.vaultKey}`
          return row.value
        }

        // return <Button size="small" onClick={() => {
        //   setEditRow(row)
        //   setEditValue(row.value)
        //   setEditVaultPath(row.vaultPath)
        //   setEditVaultKey(row.vaultKey)
        //   setEditOpen(true)
        // }}>{val()}</Button>

        return val()
      },
    },
    // {
    //   title: 'branch',
    //   dataIndex: 'branch',
    //   key: 'branch',
    //   width: '20vw',
    // },
    {
      title: 'type',
      dataIndex: 'settingType',
      key: 'settingType',
      width: '10vw',
      render: (_, row) => {
        if (row.is_casino_layer) return 'casino'
        if (row.is_env_layer) return 'env'
        return row.children?.length ? '' : SettingTypeSchema.values.find((v) => v.number === row.settingType)?.name.toLowerCase() ?? row.settingType
      },
    },
    {
      title: 'actions',
      key: 'actions',
      width: '12vw',
      render: (_, row) => {
        if (branch === 'master') return ""
        if (row.is_casino_layer) return (
          <Flex gap={4}>
            <Button size="small" danger onClick={() => {
              modal.confirm({
                title: 'Delete casino',
                content: `Delete all settings for casino "${row.casino}" on branch "${row.branch}"?`,
                okText: 'Delete',
                okType: 'danger',
                onOk: () => {
                  const flatten = (nodes: DataType[]): DataType[] =>
                    nodes.flatMap((n) => n.children?.length ? flatten(n.children) : [n])
                  const leaves = flatten(row.children ?? []).filter((l) =>
                    l.setting !== ''
                    && !l.is_env_layer && !l.is_casino_layer
                    && !l.virtual
                  )
                  if (leaves.length === 0) {
                    loadSettings()
                    return
                  }
                  const allMaster = leaves.every((l) => l.branch === 'master')
                  const call = allMaster
                    ? casinosConfig.upsertSettings({
                      settings: leaves.map((l) => ({
                        branch,
                        env: l.env,
                        casino: l.casino,
                        setting: l.setting,
                        settingType: l.settingType,
                        value: l.value,
                        vaultPath: l.vaultPath,
                        vaultKey: l.vaultKey,
                        action: Action.DELETE,
                      })),
                    })
                    : casinosConfig.deleteSettings({
                      settings: leaves.map((l) => ({
                        branch,
                        env: l.env,
                        casino: l.casino,
                        setting: l.setting,
                        settingType: l.settingType,
                      })),
                    })
                  return call
                    .then(() => loadSettings())
                    .catch((e: any) => { ErrorView(modal, allMaster ? 'upsertSettings error' : 'deleteSettings error', e) })
                },
              })
            }}>delete</Button>
          </Flex>
        )
        if (!row.children?.length) return (
          <Flex gap={4}>
            <Button size="small" onClick={() => {
              setEditRow(row)
              setEditValue(row.value)
              setEditVaultPath(row.vaultPath)
              setEditVaultKey(row.vaultKey)
              setEditOpen(true)
            }}>edit</Button>
            {!row.virtual && (
              <Button size="small" danger onClick={() => {
                modal.confirm({
                  title: 'Delete setting',
                  content: `Delete "${row.setting}" (${row.casino})?`,
                  onOk: () => {
                    const req = {
                      branch,
                      env: row.env,
                      casino: row.casino,
                      setting: row.setting,
                      settingType: row.settingType,
                      value: row.value,
                      vaultPath: row.vaultPath,
                      vaultKey: row.vaultKey,
                    }
                    if (row.branch === 'master') {
                      casinosConfig.upsertSettings({ settings: [{ ...req, action: Action.DELETE }] })
                        .then(() => loadSettings())
                        .catch((e: any) => { ErrorView(modal, 'upsertSettings error', e) })
                    } else {
                      casinosConfig.deleteSettings({ settings: [req] })
                        .then(() => loadSettings())
                        .catch((e: any) => { ErrorView(modal, 'deleteSettings error', e) })
                    }
                  },
                })
              }}>{row.branch === 'master' ? 'remove from master' : 'reset'}</Button>
            )}
          </Flex>
        )
        return ""
      },
    },
  ]

  return (
    <>
      <div style={{maxWidth: 1400, margin: '0 auto', padding: '24px 24px 40px', textAlign: 'left'}}>
      {/* Page header */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0}}>
        <h1 style={{fontSize: 32, fontWeight: 900, color: 'var(--type-primary)', margin: 0}}>
          Casino Settings
        </h1>
        {/* Settings panel toggle — opens a right-side drawer holding auxiliary controls.
            More buttons will be moved into this panel later; for now it hosts <BranchTagsWidget />. */}
        <Tooltip title="Settings">
          <Button
            icon={<SettingOutlined />}
            onClick={() => setSettingsPanelOpen(true)}
          />
        </Tooltip>
      </div>
      <Drawer
        title="Options"
        placement="right"
        open={settingsPanelOpen}
        onClose={() => setSettingsPanelOpen(false)}
      >
        <DataEntryParent text="Branch tags">
          <Button onClick={() => setBranchTagsOpen(true)}>Branch tags</Button>
        </DataEntryParent>
        <DataEntryParent text="Validate">
          <Button onClick={() => {
            casinosConfig.validateSettingsBranch({
              env: env,
              branch: branch,
              modelBranch: "master",
            })
              .then((resp) => {
                setValidationRes(resp.res)
                setValidationOpen(true)
              })
              .catch((e: any) => { ErrorView(modal, 'validateSettingsBranch error', e) })
          }}>Validate Branch</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>
            <DataEntryParent text="Delete">
              <Button onClick={() => {
                modal.confirm({
                  title: 'Delete branch',
                  content: `Delete branch "${branch}" on "${env}" and all its settings?`,
                  onOk: () => {
                    casinosConfig.deleteSettings({
                      settings: [{ branch, env }],
                    })
                      .then(() => { setBranch('master'); loadBranches(); loadSettings() })
                      .catch((e: any) => { ErrorView(modal, 'deleteModel error', e) })
                  },
                })
              }}>Delete branch</Button>
            </DataEntryParent>
            <DataEntryParent text="Import">
              <Button danger onClick={() => {
                setImportFromGitBranch('master');
                setImportAskOpen(true)
              }}>Import from git</Button>
            </DataEntryParent>
            <DataEntryParent text="token secondary">
              <Button onClick={() => setSecondaryTokensOpen(true)}>Check all secondary tokens</Button>
            </DataEntryParent>
            <DataEntryParent text="Casino">
              <Button danger onClick={() => {setAddCasinoOpen(true); setNewCasinoCheckResult(null)}}>New Casino Keys</Button>
            </DataEntryParent>
          </>
        )}
      </Drawer>
      {canMerge && (
        <BranchTagsWidget open={branchTagsOpen} onClose={() => setBranchTagsOpen(false)} />
      )}
      {/* Controls grid — 4 columns of labelled controls */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 0}}>
        <DataEntryParent text="branch">
          <StringSelector
            items={branches}
            value={[branch]}
            onChange={(value:string[]) => {
              const val = value ? value[0] : ''
              setBranch(val)
            }}
          ></StringSelector>
        </DataEntryParent>
        <DataEntryParent text="New">
          <Button onClick={() => {
            setNewBranchName('');
            setNewBranchOpen(true);
          }}>New Branch from current</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>
            <DataEntryParent text="Merge">
              <Button danger loading={mergeLoading} onClick={() => {
                const params = { env, fromBranch: branch, toBranch: 'master' }
                casinosConfig.mergeSettingsCompare(params)
                  .then((resp) => {
                    setMergeParams(params);
                    setMergeDeltas(resp.deltas ?? []);
                    setMergeOpen(true);
                  })
                  .catch((e: any) => {
                    if (String(e?.message ?? e).includes('no data for merge')) {
                      modal.warning({title: 'Merge', content: 'No data for merge'})
                    } else {
                      ErrorView(modal, 'mergeModelCompare error', e)
                    }
                  })
              }}>Merge branch</Button>
            </DataEntryParent>
            <DataEntryParent text="Tags">
              <Flex gap={8} align="center" wrap>
                <Button onClick={() => {
                  setSelectedTags(branchTags[branch] ?? [])
                  setTagsOpen(true)
                }}>Edit tags</Button>
                <Flex gap={4} wrap>
                  {(branchTags[branch] ?? []).map((t) => <Tag key={t}>{t}</Tag>)}
                </Flex>
              </Flex>
            </DataEntryParent>
          </>
        )}
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 16}}>
        <DataEntryParent text="env">
          <StringSelector
            items={envOptions}
            value={[env]}
            onChange={(value:string[]) => {
              const val = value ? value[0] : ''
              setEnv(val)
            }}
          ></StringSelector>
        </DataEntryParent>
        <DataEntryParent text="search">
          <Input.Search
            value={tableSearch}
            placeholder="search"
            allowClear
            onChange={(e) => setTableSearch(e.target.value)}
          />
        </DataEntryParent>
        <DataEntryParent text="Export">
          <Button loading={exportLoading} onClick={() => {
            setExportLoading(true);
            casinosConfig.exportSettings({ fromBranch: branch })
              .then((resp) => {
                setExportRes(resp.res)
                setExportOpen(true)
                if (resp.gitBranch || resp.gitMrLink) {
                  modal.info({
                    title: 'Export pushed to git',
                    content: (
                      <p>
                        Branch <b>{resp.gitBranch}</b> created.{' '}
                        {resp.gitMrLink && <a href={resp.gitMrLink} target="_blank" rel="noreferrer">Open merge request</a>}
                      </p>
                    ),
                  })
                }
              })
              .catch((e: any) => { ErrorView(modal, 'exportSettings error', e) })
              .finally(() => setExportLoading(false))
          }}>Export to git</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>

          </>
        )}
      </div>
      {/* Settings tree — bordered white card */}
      <div style={{
        background: 'var(--surface-white)',
        borderRadius: 'var(--radius-basic)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}>
      <Table
        // title={() => 'Casino Model configs'}
        dataSource={tableSearch ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(tableSearch.toLowerCase())) : rows}
        columns={columns}
        size={'small'}
        pagination={{ defaultPageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 500, 10000] }}
        rowClassName={(row) => row.virtual && !row.children?.length ? 'row-virtual' :
          row.branch === 'master'? '' :
            row.action === Action.DELETE ? 'merge-removed' :
              (row.is_env_layer || row.is_casino_layer) ? 'merge-changed' :
                (row.key in masterRows ? 'merge-changed' : 'merge-added')
        }
      />
      </div>
      </div>

      <Modal
        title="New Branch from current"
        open={newBranchOpen}
        onCancel={() => setNewBranchOpen(false)}
        onOk={() => {
          const originalNewBranch = newBranchName.trim()

          if (!originalNewBranch) {
            modal.warning({ title: 'Branch name is empty' })
            return
          }
          const exists = envs.some((e, i) => e === env && branches[i] === originalNewBranch)
          if (exists) {
            modal.warning({ title: 'Branch already exists', content: `"${env}:${originalNewBranch}" already exists.` })
            return
          }

          casinosConfig.copySettingsBranch({ fromBranch: branch, toBranch: originalNewBranch })
            .then(() => { setNewBranchOpen(false); loadBranches(); setBranch(originalNewBranch) })
            .catch((e: any) => { ErrorView(modal, 'copySettingsBranch error', e) })
        }}
      >
        <Input
          placeholder="New branch name"
          value={newBranchName}
          onChange={(e) => setNewBranchName(e.target.value)}
        />
      </Modal>
      <Modal
        title={`Tags for ${branch}`}
        open={tagsOpen}
        onCancel={() => setTagsOpen(false)}
        onOk={() => {
          casinosConfig.setTagsSettings({ branch, tags: selectedTags })
            .then(() => { setTagsOpen(false); loadBranches() })
            .catch((e: any) => { ErrorView(modal, 'setTagsSettings error', e) })
        }}
        okText="Save"
      >
        <Checkbox.Group
          options={AVAILABLE_TAGS.map((t) => ({ label: t, value: t }))}
          value={selectedTags}
          onChange={(vals) => setSelectedTags(vals as string[])}
        />
      </Modal>
      <Modal
        title="Edit setting"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => {
          if (!editRow) return
          const isEmpty = !editValue && !editVaultPath && !editVaultKey
          const req = {
            branch,
            env: editRow.env,
            setting: editRow.setting,
            casino: editRow.casino,
            settingType: editRow.settingType,
          }
          const call = isEmpty
            ? casinosConfig.deleteSettings({ settings: [req] })
            : casinosConfig.upsertSettings({ settings: [{ ...req, value: editValue, vaultPath: editVaultPath, vaultKey: editVaultKey }] })
          call
            .then(() => { setEditOpen(false); loadSettings() })
            .catch((e: any) => { ErrorView(modal, isEmpty ? 'deleteSettings error' : 'upsertSettings error', e) })
        }}
      >
        {editRow && (
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, alignItems: 'center' }}>
            <span >branch</span><span>{editRow.branch}</span>
            <span >env</span><span>{editRow.env}</span>
            <span >casino</span><span>{editRow.casino}</span>
            <span >setting</span><span>{editRow.setting}</span>
            <span >type</span><span>{SettingTypeSchema.values.find((v) => v.number === editRow.settingType)?.name.toLowerCase()}</span>
            {editRow.settingType === SettingType.SECRET ? (<>
              <span>vault Path</span>
              <AutoComplete
                value={editVaultPath}
                onChange={(v) => setEditVaultPath(v)}
                status={isCasinoVaultPathValid(editRow, editVaultPath) ? undefined : 'warning'}
                options={casinoVaultPathOptions(editRow)}
              />
              <span>vault Key</span>
              <AutoComplete
                value={editVaultKey}
                onChange={(v) => setEditVaultKey(v)}
                status={editVaultKey === editRow.setting.split('.').pop() ? undefined : 'warning'}
                options={[{value: editRow.setting.split('.').pop() ?? editRow.setting}]}
                style={{flex: 1}}
              />
              <span>vault Value</span>
              <Flex gap={4}>
                <Button size="small" onClick={() => {
                  casinosConfig.secretCheck({path: editVaultPath, key: editVaultKey}).then((r) => {
                    const content = `path: ${r.pathExist ? '✓' : '✗'}   key: ${r.keyExist ? '✓' : '✗'}`
                    if (!r.pathExist || !r.keyExist) { modal.warning({title: 'Secret check', content}) }
                    else { modal.info({title: 'Secret check', content}) }
                  }).catch((e: any) => { ErrorView(modal, 'secretCheck error', e) })
                }}>check</Button>
                <Button size="small" onClick={() => {
                  casinosConfig.secretCheckAndCreate({path: editVaultPath, key: editVaultKey}).then((r) => {
                    const content = `path: ${r.pathExist ? '✓' : '✗'}   key: ${r.keyExist ? '✓' : '✗'}`
                    modal.info({title: 'Secret check and create', content})
                  }).catch((e: any) => { ErrorView(modal, 'secretCheckAndCreate error', e) })
                }}>check and create</Button>
              </Flex>
            </>) : (<>
              <span >value</span>
              {editRow.settingType === SettingType.BOOL ? (
                <Select value={editValue} onChange={(v) => setEditValue(v)} options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]} />
              ) : editRow.settingType === SettingType.INT ? (
                <Input type="number" step={1} value={editValue} onChange={(e) => setEditValue(e.target.value)} />
              ) : editRow.settingType === SettingType.FLOAT ? (
                <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
              ) : (
                <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} />
              )}
            </>)}
          </div>
        )}
      </Modal>
      <Modal
        title="Merge branch comparison"
        open={mergeOpen}
        onCancel={() => setMergeOpen(false)}
        onOk={() => {
          modal.confirm({
            title: 'Merge branch',
            content: `Merge "${mergeParams.fromBranch}" into "${mergeParams.toBranch}"?`,
            onOk: () => {
              setMergeOpen(false);
              setMergeLoading(true);
              casinosConfig.mergeSettingsDo(mergeParams)
                .then((resp) => {
                  setRows([]);
                  setEnv(mergeParams.env);
                  setBranch('master');
                  loadBranches();
                  if (resp.gitBranch || resp.gitMrLink) {
                    if (resp.gitMrLink) window.open(resp.gitMrLink, '_blank')

                    modal.info({
                      title: 'Git merge required',
                      content: (
                        <p>
                          Branch <b>{resp.gitBranch}</b> needs to be merged.{' '}
                          {resp.gitMrLink && <a href={resp.gitMrLink} target="_blank" rel="noreferrer">Open merge request</a>}
                        </p>
                      ),
                    })
                  } else {
                    modal.info({
                      title: 'Merge completed',
                      content: (
                        <p>
                          Updated <b>{resp.deltas.length}</b> setting{resp.deltas.length === 1 ? '' : 's'}.
                        </p>
                      ),
                    })
                  }
                })
                .catch((e: any) => { ErrorView(modal, 'mergeSettingsDo error', e) })
                .finally(() => setMergeLoading(false))
            },
          })
        }}
        okText="Merge"
        // Merge is only offered to users allowed to perform it on the backend.
        okButtonProps={{ style: canMerge ? undefined : { display: 'none' } }}
        width="90vw"
      >
        <Table
          size="small"
          pagination={{ pageSize: 25 }}
          rowKey={(_, i) => String(i)}
          rowClassName={(d) => !d.valueTo ? 'merge-added' : !d.valueFrom ? 'merge-removed' : 'merge-changed'}
          dataSource={mergeDeltas}
          columns={[
            { title: 'env', dataIndex: 'env', key: 'env' },
            { title: 'casino', dataIndex: 'casino', key: 'casino' },
            { title: 'setting', dataIndex: 'setting', key: 'setting' },
            { title: 'before', dataIndex: 'valueTo', key: 'valueTo', render: (v) => v || <span style={{ color: '#999' }}>&lt;default&gt;</span> },
            { title: 'new', dataIndex: 'valueFrom', key: 'valueFrom', render: (v) => v || <span style={{ color: '#999' }}>&lt;default&gt;</span> },
          ]}
        />
      </Modal>
      <Modal
        title="New Casino Keys"
        width={800}
        open={addCasinoOpen}
        onCancel={() => setAddCasinoOpen(false)}
        footer={<Button onClick={() => setAddCasinoOpen(false)}>Close</Button>}
      >
        {(() => {
          const flatten = (nodes: DataType[]): DataType[] =>
            nodes.flatMap((n) => n.children?.length ? flatten(n.children) : [n])
          const flatRows = flatten(rows)
          const authTokenSettings = new Set([token_settings_main_name, token_settings_secondary_name])
          const valueSet = new Set(
            flatRows
              .filter((r) => authTokenSettings.has(r.setting) && r.vaultPath.trim() !== '' && r.vaultKey.trim() !== '')
              .map((r) => r.vaultPath)
          )
          const options = Array.from(valueSet).map((v) => ({ value: v, label: v }))

          const casinoDT = rows.find((r) => r.casino === newCasinoId && r.env === env)
          const mainKeySettingExists = flatRows.some((r) => r.casino === newCasinoId && r.env === env && r.setting === token_settings_main_name && !r.virtual)
          const secondaryKeySettingExists = flatRows.some((r) => r.casino === newCasinoId && r.env === env && r.setting === token_settings_secondary_name && !r.virtual)

          return (
            <div>
            <ol style={{ marginBottom: 16, paddingLeft: 20, color: '#555', fontSize: 13 }}>
              <li>Enter the <b>vault path</b> — the path where casino tokens will be stored in Vault (e.g. <code>secret/casinos/my-casino</code>). The last segment becomes the casino ID.</li>
              <li>Click <b>Check availability</b> to verify the path is not already taken and see which keys exist.</li>
              <li>If the main key does not exist, click <b>Create</b> next to "vault main key" to generate it.</li>
              <li>If you plan to use a two-key scheme, also create the secondary key.</li>
              <li>If the casino ID should differ from the last path segment, edit it manually in the <b>casino</b> field.</li>
              <li>Once the vault key exists, click <b>Create</b> next to "setting main key" to save the setting in the database.</li>
              <li>If a secondary key was created in Vault, also click <b>Create</b> next to "setting secondary key" to save it in the database.</li>
              <li>Once all steps are done, close this window and continue editing the casino parameters in the main form. When finished, don't forget to <b>merge your changes</b> to apply them and <b>deploy</b> them!</li>
            </ol>
            <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 12px', alignItems: 'center' }}>
              <span>env</span>
              <StringSelector
                items={envOptions}
                value={[env]}
                onChange={(value:string[]) => {
                  const val = value ? value[0] : ''
                  setEnv(val)
                }}
              ></StringSelector>
              <span /><span />
              <span>vault path</span>
              <Flex gap={8}>
                <AutoComplete
                  style={{ flex: 1 }}
                  options={options}
                  placeholder="type path"
                  value={newCasinoPath}
                  onChange={(v) => { setNewCasinoPath(v.trim()); setNewCasinoId(v.trim().split('/').at(-1) ?? ''); setNewCasinoCheckResult(null) }}
                  filterOption={(input, option) => (option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Button type={newCasinoPath && !newCasinoCheckResult ? 'primary' : 'default'} onClick={() => {
                  if (!newCasinoId) {
                    modal.error({ title: 'Error', content: 'Casino ID is empty' })
                    return
                  }
                  casinoKeyService.newCasinoKey({
                    casinoId: newCasinoId,
                    pathVault: newCasinoPath,
                    createMainKey: false,
                    createSecondaryKey: false,
                  })
                    .then((resp: NewCasinoKeyResponse) => setNewCasinoCheckResult(resp))
                    .catch((e: any) => { ErrorView(modal, 'newCasinoKey error', e) })
                }}>Check availability</Button>
              </Flex>
              <span>vault main key</span>
              <Flex align="center" gap={8}>
                <span>{newCasinoCheckResult ? (newCasinoCheckResult.mainKeyExists ? '✓ exists' : '✗ not found') : '—'}</span>
                {newCasinoCheckResult && !newCasinoCheckResult?.mainKeyExists && (
                  <Button size="small" type={'primary'} onClick={() => {
                    modal.confirm({
                      title: 'Create main key',
                      content: `Create main key for "${newCasinoId}" at path "${newCasinoPath}"? This action cannot be undone automatically — changes can only be reverted manually.`,
                      okText: 'Create',
                      okType: 'danger',
                      onOk: () => casinoKeyService.newCasinoKey({
                        casinoId: newCasinoId,
                        pathVault: newCasinoPath,
                        createMainKey: true,
                        createSecondaryKey: false
                      })
                        .then((resp: NewCasinoKeyResponse) => {
                          setNewCasinoCheckResult(resp)
                        })
                        .catch((e: any) => { ErrorView(modal, 'newCasinoKey error', e) }),
                    })
                  }}>Create</Button>
                )}
              </Flex>
              <span>vault secondary key</span>
              <Flex align="center" gap={8}>
                <span>{newCasinoCheckResult ? (newCasinoCheckResult.secondaryKeyExists ? '✓ exists' : '✗ not found') : '—'}</span>
                {newCasinoCheckResult && newCasinoCheckResult?.mainKeyExists && !newCasinoCheckResult?.secondaryKeyExists && (
                  <Button size="small" onClick={() => {
                    modal.confirm({
                      title: 'Create secondary key',
                      content: `Create secondary key for "${newCasinoId}" at path "${newCasinoPath}"? This action cannot be undone automatically — changes can only be reverted manually.`,
                      okText: 'Create',
                      okType: 'danger',
                      onOk: () => casinoKeyService.newCasinoKey({
                        casinoId: newCasinoId,
                        pathVault: newCasinoPath,
                        createMainKey: false,
                        createSecondaryKey: true
                      })
                        .then((resp: NewCasinoKeyResponse) => {
                          setNewCasinoCheckResult(resp)
                        })
                        .catch((e: any) => { ErrorView(modal, 'newCasinoKey error', e) }),
                    })
                  }}>Create</Button>
                )}
              </Flex>
              <span /><span />
              <span>casino</span>
              <Input value={newCasinoId} onChange={(e) => setNewCasinoId(e.target.value)} />
              <span>casino in db</span>
              <span>{casinoDT ? '✓ exists' : '✗ not found'}</span>
              <span>setting main key</span>
              <Flex align="center" gap={8}>
                <span>{mainKeySettingExists ? '✓ exists' : '✗ not found'}</span>
                {!mainKeySettingExists && env && newCasinoCheckResult?.mainKeyExists && (
                  <Button size="small" type={'primary'} onClick={() => {
                    casinosConfig.upsertSettings({ settings: [{
                        branch,
                        env,
                        casino: newCasinoId,
                        setting: token_settings_main_name,
                        vaultPath: newCasinoPath,
                        vaultKey: token_main_name,
                      }, {
                        branch,
                        env,
                        casino: newCasinoId,
                        setting: 'identifier',
                        value: newCasinoId,
                      }, {
                        branch,
                        env,
                        casino: newCasinoId,
                        setting: 'title',
                        value: newCasinoId,
                      }, {
                        branch,
                        env,
                        casino: newCasinoId,
                        setting: 'settings.server_url',
                        value: 'https://link',
                      }]
                    })
                      .then(() => { loadSettings(); modal.success({ title: 'Done', content: token_settings_main_name + ' set.' }) })
                      .catch((e: any) => { ErrorView(modal, 'upsertSettings error', e) })
                  }}>Create</Button>
                )}
              </Flex>
              <span>setting secondary key</span>
              <Flex align="center" gap={8}>
                <span>{secondaryKeySettingExists ? '✓ exists' : '✗ not found'}</span>
                {!secondaryKeySettingExists && env && newCasinoCheckResult?.secondaryKeyExists && (
                  <Button size="small" onClick={() => {
                    casinosConfig.upsertSettings({ settings: [{
                        branch,
                        env,
                        casino: newCasinoId,
                        setting: token_settings_secondary_name,
                        vaultPath: newCasinoPath,
                        vaultKey: token_secondary_name,
                    }] })
                      .then(() => { loadSettings(); modal.success({ title: 'Done', content: token_settings_secondary_name + ' set.' }) })
                      .catch((e: any) => { ErrorView(modal, 'upsertSettings error', e) })
                  }}>Create</Button>
                )}
              </Flex>
              <span />

            </div>
            </div>
          )
        })()}
      </Modal>

      <Modal
        title="Check all secondary tokens"
        width={800}
        open={secondaryTokensOpen}
        onCancel={() => setSecondaryTokensOpen(false)}
        footer={<Button onClick={() => setSecondaryTokensOpen(false)}>Close</Button>}
      >
        {(() => {
          const flatten = (nodes: DataType[]): DataType[] =>
            nodes.flatMap((n) => n.children?.length ? flatten(n.children) : [n])
          const flatRows = flatten(rows)

          // Collect all (env, casino) pairs and detect which auth token settings exist.
          type TokenRow = { key: string; env: string; casino: string; mainExists: boolean; secondaryExists: boolean }
          const byCasino = new Map<string, TokenRow>()
          for (const r of flatRows) {
            if (!r.casino) continue
            const key = `${r.env}:${r.casino}`
            let row = byCasino.get(key)
            if (!row) {
              row = { key, env: r.env, casino: r.casino, mainExists: false, secondaryExists: false }
              byCasino.set(key, row)
            }
            if (!r.virtual && r.setting === token_settings_main_name) row.mainExists = true
            if (!r.virtual && r.setting === token_settings_secondary_name) row.secondaryExists = true
          }

          // Skip casinos that already have both tokens.
          const tokenRows = Array.from(byCasino.values())
            .filter((r) => !(r.mainExists && r.secondaryExists))
            .sort((a, b) => a.env.localeCompare(b.env) || a.casino.localeCompare(b.casino))

          // Ensure both vault keys exist for the casino, then persist the missing token settings.
          const createTokensCore = (r: TokenRow) =>
            casinoKeyService.newCasinoKey({
              casinoId: r.casino,
              createMainKey: false,
              createSecondaryKey: false,
            })
              .then((resp: NewCasinoKeyResponse) => {
                // Invert missing flags and create only the keys that are not there yet.
                const createMainKey = !resp.mainKeyExists
                const createSecondaryKey = !resp.secondaryKeyExists
                const ensured = (createMainKey || createSecondaryKey)
                  ? casinoKeyService.newCasinoKey({
                      casinoId: r.casino,
                      pathVault: resp.pathVault,
                      createMainKey,
                      createSecondaryKey,
                    })
                  : Promise.resolve(resp)
                return ensured.then((final: NewCasinoKeyResponse) => {
                  // Persist only the token settings that are missing for the casino.
                  const settings = []
                  if (!r.mainExists) {
                    settings.push({
                      branch,
                      env: r.env,
                      casino: r.casino,
                      setting: token_settings_main_name,
                      vaultPath: final.pathVault,
                      vaultKey: token_main_name,
                    })
                  }
                  if (!r.secondaryExists) {
                    settings.push({
                      branch,
                      env: r.env,
                      casino: r.casino,
                      setting: token_settings_secondary_name,
                      vaultPath: final.pathVault,
                      vaultKey: token_secondary_name,
                    })
                  }
                  if (settings.length === 0) return
                  return casinosConfig.upsertSettings({ settings }).then(() => {})
                })
              })

          const createTokens = (r: TokenRow) => {
            setCreateTokensLoadingKey(r.key)
            createTokensCore(r)
              .then(() => { loadSettings(); modal.success({ title: 'Done', content: `${r.casino}: tokens set.` }) })
              .catch((e: any) => { ErrorView(modal, 'create tokens error', e) })
              .finally(() => setCreateTokensLoadingKey(null))
          }

          const createAllTokens = () => {
            setCreateTokensLoadingKey('__all__')
            // Run casinos sequentially to avoid overloading Vault/GitLab.
            tokenRows.reduce(
              (chain, r) => chain.then(() => createTokensCore(r)),
              Promise.resolve() as Promise<unknown>,
            )
              .then(() => { loadSettings(); modal.success({ title: 'Done', content: `Tokens set for ${tokenRows.length} casino(s).` }) })
              .catch((e: any) => { ErrorView(modal, 'create all tokens error', e) })
              .finally(() => setCreateTokensLoadingKey(null))
          }

          return (
            <>
            {tokenRows.length > 0 && (
              <Flex justify="flex-end" style={{ marginBottom: 12 }}>
                <Button
                  type="primary"
                  loading={createTokensLoadingKey === '__all__'}
                  disabled={createTokensLoadingKey !== null && createTokensLoadingKey !== '__all__'}
                  onClick={createAllTokens}
                >create all tokens</Button>
              </Flex>
            )}
            <Table
              size="small"
              pagination={{ pageSize: 25 }}
              rowKey={(r) => r.key}
              dataSource={tokenRows}
              columns={[
                { title: 'env', dataIndex: 'env', key: 'env' },
                { title: 'casinoid', dataIndex: 'casino', key: 'casino' },
                { title: token_settings_main_name, dataIndex: 'mainExists', key: 'mainExists', render: (v) => v ? '✓' : '✗' },
                { title: token_settings_secondary_name, dataIndex: 'secondaryExists', key: 'secondaryExists', render: (v) => v ? '✓' : '✗' },
                { title: 'action', key: 'action', render: (_, r) => (
                  <Button
                    size="small"
                    type="primary"
                    loading={createTokensLoadingKey === r.key}
                    disabled={createTokensLoadingKey !== null && createTokensLoadingKey !== r.key}
                    onClick={() => createTokens(r)}
                  >create tokens</Button>
                ) },
              ]}
            />
            </>
          )
        })()}
      </Modal>

      <ResultModal
        title={`Validation — ${branch}`}
        open={validationOpen}
        res={validationRes as any}
        onClose={() => setValidationOpen(false)}
      />

      <ResultModal
        title={`Export — ${branch}`}
        open={exportOpen}
        res={exportRes as any}
        onClose={() => setExportOpen(false)}
      />

      <Modal
        title="Import from git"
        open={importAskOpen}
        onCancel={() => setImportAskOpen(false)}
        confirmLoading={importLoading}
        cancelButtonProps={{disabled: importLoading}}
        maskClosable={!importLoading}
        closable={!importLoading}
        onOk={() => {
          const fromGitBranch = importFromGitBranch.trim() || 'master'
          setImportLoading(true);
          casinosConfig.importSettings({ fromGitBranch: fromGitBranch, toBranch: branch })
            .then((resp) => {
              setImportAskOpen(false);
              setImportRes(resp.res);
              setImportOpen(true)
              loadSettings()
            })
            .catch((e: any) => { ErrorView(modal, 'importSettings error', e) })
            .finally(() => setImportLoading(false))
        }}
        okText="Import"
      >
        <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
          <span style={{fontWeight: 500}}>from git branch</span>
          <Input
            placeholder="master"
            value={importFromGitBranch}
            onChange={(e) => setImportFromGitBranch(e.target.value)}
          />
          <span style={{fontWeight: 500}}>to branch</span>
          <span>{branch}</span>
        </div>
      </Modal>

      <ResultModal
        title={`Import from git — ${branch}`}
        open={importOpen}
        res={importRes as any}
        onClose={() => {
          setImportOpen(false);
          loadSettings()
        }}
      />
      {contextHolder}
    </>
  )
}
